import { Trans } from "react-i18next"
import { db } from "../../../data/db/db"
import { ArticleSpecs } from "../../../models/articleModel"
import { getMaterialGroups } from "../../calculator/components/formHelpers"
import { ToolfinderFilterForm, ToolfinderStandardData, ToolfinderTrocutData } from "./FormTypes"


//Iso Rating für Sortierung nach Material
const isoRating = {
    p: { p: 3, k: 2, h: 2, m: 1, s: 1, n: 1, c: 1 },
    k: { p: 2, k: 3, h: 2, m: 1, s: 1, n: 1, c: 1 },
    h: { p: 2, k: 2, h: 3, m: 1, s: 1, n: 1, c: 1 },
    m: { p: 1, k: 1, h: 1, m: 1, s: 2, n: 1, c: 1 },
    s: { p: 1, k: 1, h: 1, m: 1, s: 3, n: 1, c: 1 },
    n: { p: 1, k: 1, h: 1, m: 1, s: 1, n: 3, c: 2 },
    c: { p: 1, k: 1, h: 1, m: 1, s: 1, n: 2, c: 3 },
}

// Für Formular-Validierung
export const getMaxAPValue = (strategy: string, diameter: number, ae: number) => {
    switch (strategy) {
        case "std-vsp":
            return ({
                value: ae,
                message: (<Trans
                    i18nKey="toolfinder.formHelpers.apLimitMessage1"
                    components={{ 1: <div />, 2: <sub /> }}
                />)
            })
        case "std-bes":
            if (ae <= (0.3 * diameter)) {
                return ({
                    value: 100,
                    message: (<Trans
                        i18nKey="toolfinder.formHelpers.apLimitMessage2"
                        components={{ 1: <div />, 2: <sub /> }}
                    />)
                })
            } else if (ae <= (0.6 * diameter)) {
                return ({
                    value: 1.5 * diameter,
                    message: (<Trans
                        i18nKey="toolfinder.formHelpers.apLimitMessage3"
                        components={{ 1: <div />, 2: <sub /> }}
                    />)
                })
            } else {
                return ({
                    value: diameter,
                    message: (<Trans
                        i18nKey="toolfinder.formHelpers.apLimitMessage4"
                        components={{ 1: <div />, 2: <sub /> }}
                    />)
                })
            }
        default: return ({
            value: 100,
            message: (<Trans
                i18nKey="toolfinder.formHelpers.apLimitMessage2"
                components={{ 1: <div />, 2: <sub /> }}
            />)
        })
    }
}

// Findet passende Fräser wenn Methode standard ist
export const findStandardTool = async (data: ToolfinderStandardData) => {
    const d1 = data.diameter.toString();
    let specs = await db.articlesSpec
        .where("d1").equals(d1)
        .and((miller) => { return Number(miller.l2) >= data.ap })
        .and((miller) => miller.typ.toLowerCase().includes("form") === false)
        .toArray();

    if (data.strategy === "std-ko") {
        specs = specs.filter(spec => {
            return spec.ident[2] === "1" || spec.ident[2] === "2"
        })
    }

    const cuttingSpecs = await db.articleCuttingSpecsStd.toArray();

    let res: ArticleSpecs[] = []
    specs.forEach((spec) => {
        let filteredCuttingSpecs = cuttingSpecs.filter((cuttingsSpec) => cuttingsSpec.ident === spec.ident)
        filteredCuttingSpecs.forEach((filteredCuttingSpec) => {
            let combinedSpec = { ...spec }
            combinedSpec.cuttingSpec = { ...filteredCuttingSpec }
            res.push(combinedSpec)
        })
    })

    const sorted = sortByMaterial(res, data.material)

    return sorted;
}


//Sortiert Fräser danach, wie gut sie zum Material passen
const sortByMaterial = (res: ArticleSpecs[], selectedMaterial: string) => {
    //Sortiert nicht geeigenet Fräser für das gewählte Material aus
    let matRes = res.filter((entry) => {
        /* @ts-ignore */
        let matGroup = getMaterialGroups(entry.cuttingSpec!)
        return matGroup.includes(selectedMaterial)
    })

    //Iso Key von gewählte Material
    const matKey = selectedMaterial.charAt(0).toLowerCase() as keyof typeof isoRating;
    const isoKey = "iso_" + matKey;

    // gefilterte Fräser werden um das Feld rating erweitert
    // rating gibt Ausschluss darüber, wie passend das Material ist (basierend auf isoRating Objekt oben)
    matRes.forEach(miller => {
        const hauptgruppe = miller.iso_hauptgruppe.toLowerCase() as keyof typeof isoRating;
        const isoRatingValue = isoRating[matKey][hauptgruppe] ? isoRating[matKey][hauptgruppe] : 0
        miller.rating = isoRatingValue;
    })

    // Sortiere nach 1. Rating und 2. genaue hauptgruppe (zb P1) 
    const sorted = matRes.sort((a, b) => {
        /* @ts-ignore */
        return b.rating! - a.rating! || b[isoKey]! - a[isoKey]!
    })

    return sorted
}

// Findet passende Fräser wenn Methode trocut ist
export const findTrocutTool = async (data: ToolfinderTrocutData) => {
    const d1 = data.min_r * 2;
    let specs: ArticleSpecs[] = [];
    if (data.method === "inAndOut") {
        specs = await db.articlesSpec
            .where("trocut").equals("1")
            .and((miller) => { return Number(miller.l2) >= data.ap })
            .and((miller) => { return Number(miller.d1) <= d1 })
            .toArray();
    } else if (data.method === "onlyOut") {
        specs = await db.articlesSpec
            .where("trocut").equals("1")
            .and((miller) => { return Number(miller.l2) >= data.ap })
            .toArray();
    }

    const cuttingSpecsTro = await db.articleCuttingSpecsTro.toArray();

    let res: ArticleSpecs[] = []
    for (const spec of specs) {
        const filteredCuttingSpecsTro = cuttingSpecsTro.filter((cuttingsSpec) => cuttingsSpec.ident === spec.ident)
        for (const filteredCuttingSpec of filteredCuttingSpecsTro) {
            const cuttingSpecStd = await db.articleCuttingSpecsStd
                .where("ident").equals(filteredCuttingSpec.ident)
                .and((miller) => miller.laengen === filteredCuttingSpec.laengen)
                .and(miller => miller.spezialset === filteredCuttingSpec.spezialset)
                .first();
            let combinedSpec = { ...spec }
            combinedSpec.cuttingSpecTro = { ...filteredCuttingSpec }

            /* @ts-ignore */
            combinedSpec.cuttingSpec = { ...cuttingSpecStd }
            res.push(combinedSpec)
        }
    }

    const sorted = sortByMaterial(res, data.material)

    return sorted;
}

// TODO Mehrsprachigkeit
export const mapTrocutMethod = (method: string) => {
    switch (method) {
        case "inAndOut": return "Innen und Außen"
        case "onlyOut": return "Nur Außen"
        default: return ""
    }
}

// wendet Filter für Fräser von Dialog an
export const applyFilter = (res: ArticleSpecs[], filter: ToolfinderFilterForm) => {
    /* Schneidenlänge l2min */
    if (filter.l2min !== "") {
        res = res.filter(articleSpec => Number(articleSpec.l2) >= Number(filter.l2min))
    }
    /* Schneidenlänge l2max */
    if (filter.l2max !== "") {
        res = res.filter(articleSpec => Number(articleSpec.l2) <= Number(filter.l2max))
    }
    /* Anzahl Schneiden */
    if (filter.z !== "") {
        res = res.filter(articleSpec => articleSpec.z.toString() === filter.z)
    }
    /* Verzahnungs-Typ */
    if (filter.ttype !== "") {
        res = res.filter(articleSpec => articleSpec.ttype === filter.ttype)
    }
    /* Schneidkante */
    if (filter.cuttingEdge !== "") {
        if (filter.cuttingEdge === "fase") {
            res = res.filter(articleSpec => articleSpec.din4000_f72 === "2")
        } else if (filter.cuttingEdge === "radius") {
            res = res.filter(articleSpec => articleSpec.din4000_f72 === "1")
        } else if (filter.cuttingEdge === "scharfkantig") {
            // manche Fräser sind mit Fase angegeben, haben aber einen Unterartikel mit /C0 der angibt, dass der Fräser doch scharfkantig ist
            res = res.filter(articleSpec => articleSpec.din4000_f72 === "5" || articleSpec.pos.endsWith("/C0"))
        }
    }
    /* Beschichtung */
    if (filter.coating !== "") {
        if (filter.coating === "coated") {
            res = res.filter(articleSpec => articleSpec.besch !== null)
        } else if (filter.coating === "notCoated") {
            res = res.filter(articleSpec => articleSpec.besch === null)
        }
    }
    /* Schaft (Weldon) */
    if (filter.weldon !== "") {
        if (filter.weldon === "weldon") {
            res = res.filter(articleSpec => articleSpec.weldon.toString() === "1")
        } else if (filter.weldon === "smooth") {
            res = res.filter(articleSpec => articleSpec.weldon.toString() === "0")
        }
    }
    /* Innenkühlung */
    if (filter.ik !== "") {
        // TODO
        // values: withIk, withoutIk
        if (filter.ik === "withIk") {
            res = res.filter(articleSpec => articleSpec.ik !== null)
        } else if (filter.ik === "withoutIk") {
            res = res.filter(articleSpec => articleSpec.ik === null)
        }
    }
    return res;
}

export const getActiveFilterAmount = (filter: ToolfinderFilterForm) => {
    let counter = 0;
    Object.entries(filter).forEach(([, value]) => {
        if (value !== "") {
            counter++
        }
    })

    return counter
}

export const getl2MaxMinValue = (ap: string, l2min: string) => {
    let minValue = 0.1;
    if (ap) {
        minValue = Number(ap)
    }
    if (Number(l2min) > minValue) {
        minValue = Number(l2min)
    }
    return minValue;
}

